/** Dialog to create a new form when template + enrollment period are fixed (e.g. from forms list). User selects the student. */
import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { EligibleStudentSelectInput } from "src/components/Inputs/EligibleStudentSelectInput";
import { EnrollmentPeriodSelectInput } from "src/components/Inputs/EnrollmentPeriodSelectInput";
import { Glossary } from "src/components/Text/Glossary";
import type { ApplicantSummary } from "src/generated/hooks/form-service/requests";
import { validateWithZod } from "src/services/formValidations";
import { CreateFormPreRankingPhase } from "./CreateFormPreRankingPhase";
import { useCreateFormForTemplateDialog } from "./useCreateFormForTemplateDialog";
import {
  CreateFormFormSchema,
  CreateFormFormValues,
} from "./useCreateFormSubmit";

interface CreateFormForTemplateDialogProps {
  enrollmentPeriodId: uuid;
  formTemplateId: uuid;
  formTemplateName: string;
  isOpen: boolean;
  onClose: () => void;
  width?: string;
  height?: string;
  // Reopen seeds from the Add family round trip: a just-created student to
  // preselect, or the original search text to restore.
  initialSelectedStudent?: ApplicantSummary | null;
  initialSearchText?: string;
}

export function CreateFormForTemplateDialog({
  enrollmentPeriodId,
  formTemplateId,
  formTemplateName,
  isOpen,
  onClose,
  width,
  height,
  initialSelectedStudent,
  initialSearchText,
}: CreateFormForTemplateDialogProps) {
  const {
    organizationId,
    isSchoolAdmin,
    glossaryTransform,
    initialValues,
    onSubmit,
    isDecisionLoading,
    inCreatePreRanking,
    isBusy,
    student,
    school,
    precheck,
    notEligible,
    createNew,
    actions,
  } = useCreateFormForTemplateDialog({
    enrollmentPeriodId,
    formTemplateId,
    formTemplateName,
    isOpen,
    onClose,
    initialSelectedStudent,
  });

  const schoolSelect = (
    <FormControl isRequired>
      <FormLabel>
        <Glossary>Which school are you creating this response for?</Glossary>
      </FormLabel>
      <Select
        placeholder={glossaryTransform("Select a school")}
        value={school.selectedId}
        onChange={(event) => school.setSelectedId(event.target.value)}
      >
        {school.options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Select>
      {!school.selectedId && (
        <FormHelperText>
          <Glossary>
            Select the school you are creating this response for.
          </Glossary>
        </FormHelperText>
      )}
    </FormControl>
  );

  return (
    <Formik<CreateFormFormValues>
      // Remount a fresh Formik on each open so a stale applicantId cannot linger
      // between opens (which would make reselecting the same student a no-op).
      key={isOpen ? "open" : "closed"}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validate={validateWithZod(CreateFormFormSchema)}
      validateOnBlur
      validateOnMount
      validateOnChange
    >
      {({ isValid, resetForm }) => {
        const handleOnClose = () => actions.close(resetForm);
        return (
          <Modal
            isCentered
            // Only the pre-ranking phase can grow tall enough to need scrolling;
            // cap and inner-scroll it there. The student phase keeps the default
            // behavior so its search dropdown still overflows above the modal.
            scrollBehavior={inCreatePreRanking ? "inside" : "outside"}
            isOpen={isOpen}
            onClose={handleOnClose}
          >
            <ModalOverlay />
            <ModalContent
              as={Form}
              noValidate
              width={width}
              height={height}
              maxH={inCreatePreRanking ? "60vh" : undefined}
            >
              <ModalHeader
                borderBottom={inCreatePreRanking ? "1px solid" : undefined}
                borderColor="gray.200"
              >
                Create new form
              </ModalHeader>

              {inCreatePreRanking ? (
                <CreateFormPreRankingPhase
                  formTemplateId={formTemplateId}
                  applicant={student.preRankingApplicant}
                  schoolSelect={schoolSelect}
                  schoolSelectedId={school.selectedId}
                  isBusy={isBusy}
                  notEligibleOpen={notEligible.isOpen}
                  studentName={student.name}
                  onBack={() => actions.setPhase("student")}
                  onCloseNotEligible={() => notEligible.setIsOpen(false)}
                  onDone={handleOnClose}
                  onSubmit={actions.submitPreRanking}
                />
              ) : (
                <>
                  <ModalBody as={VStack} padding={6} pb={4}>
                    {renderStudentPhase()}
                  </ModalBody>
                  <ModalFooter gap={2} pt={2} pb={4}>
                    <Button
                      onClick={() => {
                        amplitude.track("Cancel create form clicked");
                        if (notEligible.isOpen) {
                          notEligible.setIsOpen(false);
                        } else {
                          handleOnClose();
                        }
                      }}
                      variant="ghost"
                    >
                      {notEligible.isOpen ? "Back" : "Cancel"}
                    </Button>
                    {renderPrimaryButton()}
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        );

        // Student-selection phase. The student picker stays mounted across the
        // add-to-existing and not-eligible states so selecting a student never
        // clears or flashes; the branch-specific content renders below it.
        function renderStudentPhase() {
          return (
            <Flex direction="column" gap={4} width="100%">
              <EligibleStudentSelectInput
                name="applicantId"
                organizationId={organizationId}
                onSelectedStudent={student.onSelect}
                createNewStudentReturn={student.createNewStudentReturn}
                canCreateStudent={student.canCreateStudent}
                onCreateNewStudent={handleOnClose}
                initialSelectedStudent={initialSelectedStudent}
                initialSearchText={initialSearchText}
                isRequired
              />

              <EnrollmentPeriodSelectInput
                name="enrollmentPeriodId"
                organizationId={organizationId}
                isRequired
                placeholder="Select enrollment period"
                label="Enrollment period"
                isDisabled
                isVisible={false}
              />

              {renderStudentPhaseContent()}
            </Flex>
          );
        }

        // The content shown beneath the student picker, resolved in priority
        // order: not-eligible alert, loading spinner, add-to-existing branch,
        // then the create-new (non-school-admin) alert.
        function renderStudentPhaseContent() {
          if (notEligible.isOpen) {
            return (
              <InfoAlert status="info">
                <Glossary>
                  {`${student.name} is not eligible for this school. Contact the organization's administrator for help.`}
                </Glossary>
              </InfoAlert>
            );
          }

          if (isDecisionLoading) {
            return (
              <Flex justifyContent="center" paddingY={4}>
                <Spinner />
              </Flex>
            );
          }

          if (precheck.isAddToExisting) {
            return (
              <>
                {schoolSelect}
                {renderAddToExistingAlert()}
              </>
            );
          }

          if (school.hasNoStaffSchools) {
            return (
              <InfoAlert status="info">
                <Glossary>
                  None of your schools are offered in this enrollment period, so
                  this form can't be created. Contact an organization
                  administrator for help.
                </Glossary>
              </InfoAlert>
            );
          }

          if (!createNew.alertText) {
            return null;
          }
          return (
            <InfoAlert status={createNew.alertStatus}>
              <Text fontSize="md">
                {createNew.alertText}
                {createNew.showSeeFormLink && (
                  <Link
                    variant="link"
                    onClick={() => createNew.onSeeFormClick(handleOnClose)}
                    color="blue.900"
                    fontWeight="700"
                    fontSize="md"
                    textDecoration="underline"
                    paddingLeft="2"
                  >
                    See form.
                  </Link>
                )}
              </Text>
            </InfoAlert>
          );
        }

        // Add-to-existing alert ladder. Predicate order must match
        // renderPrimaryButton's so the body message and footer button never
        // disagree (max-reached is terminal and is checked before the
        // school-selection and already-on-form states).
        function renderAddToExistingAlert() {
          if (school.options.length === 0) {
            return (
              <InfoAlert status="info">
                <Glossary>
                  {`${student.name} already has a form and none of your schools can be added. Contact an organization administrator for help.`}
                </Glossary>
              </InfoAlert>
            );
          }

          if (precheck.isMaxReached) {
            return (
              <InfoAlert status="info">
                <Glossary>
                  {`${student.name} has a form response for a different school and has already reached the maximum amount of schools that can be ranked. Contact the organization's administrator for help.`}
                </Glossary>
              </InfoAlert>
            );
          }

          // The prompt to pick a school is a caption on the Select itself, so no
          // separate alert is needed while it is unselected.
          if (!school.selectedId) {
            return null;
          }

          if (precheck.selectedSchoolOnForm) {
            return (
              <InfoAlert status="warning">
                <Text fontSize="md">
                  <Glossary>
                    {`${student.name} already has a response for this form with this school.`}
                  </Glossary>
                  <Link
                    variant="link"
                    onClick={() => {
                      if (precheck.existingFormId) {
                        actions.staffClose();
                        actions.navigateToForm(precheck.existingFormId);
                      }
                    }}
                    color="blue.900"
                    fontWeight="700"
                    fontSize="md"
                    textDecoration="underline"
                    paddingLeft="2"
                  >
                    See form.
                  </Link>
                </Text>
              </InfoAlert>
            );
          }

          return (
            <InfoAlert status="info">
              <Glossary>
                {`${student.name} already has a form. Your school will be added to it.`}
              </Glossary>
            </InfoAlert>
          );
        }

        function renderPrimaryButton() {
          if (notEligible.isOpen) {
            return (
              <Button type="button" onClick={handleOnClose}>
                Done
              </Button>
            );
          }

          if (precheck.isAddToExisting) {
            if (school.options.length === 0 || precheck.isMaxReached) {
              return (
                <Button type="button" onClick={handleOnClose}>
                  Done
                </Button>
              );
            }
            if (!school.selectedId) {
              return null;
            }
            if (precheck.selectedSchoolOnForm) {
              return (
                <Button
                  type="button"
                  isDisabled={!precheck.existingFormId || precheck.isPending}
                  onClick={() => {
                    if (precheck.existingFormId && !precheck.isPending) {
                      actions.staffClose();
                      actions.navigateToForm(precheck.existingFormId);
                    }
                  }}
                >
                  See form
                </Button>
              );
            }
            return (
              <Button
                type="button"
                isLoading={isBusy || precheck.isPending}
                isDisabled={!precheck.existingFormId || precheck.isPending}
                onClick={() => {
                  amplitude.track("Add school to existing form clicked");
                  actions.addToExisting();
                }}
              >
                Create
              </Button>
            );
          }

          if (isSchoolAdmin) {
            return (
              <Button
                type="button"
                isLoading={
                  createNew.assignedFormsRD.isLoading() || precheck.isPending
                }
                isDisabled={
                  !createNew.canStartForm ||
                  !isValid ||
                  precheck.isPending ||
                  school.hasNoStaffSchools
                }
                onClick={() => {
                  amplitude.track("Create form next clicked");
                  actions.setPhase("preRanking");
                }}
              >
                Next
              </Button>
            );
          }

          return (
            <Button
              type="submit"
              isLoading={
                createNew.assignedFormsRD.isLoading() || createNew.isSubmitting
              }
              isDisabled={!createNew.canStartForm || !isValid}
              onClick={() => amplitude.track("Create form clicked")}
            >
              Create
            </Button>
          );
        }
      }}
    </Formik>
  );
}
